import { useEffect, useState, useRef } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function ChatWindow({ open, onClose, partner }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    if (!partner) return;
    try { const r = await api.get(`/messages/thread/${partner.user_id}`); setMessages(r.data); } catch (err) { console.error("chat load failed", err); }
  };
  useEffect(() => {
    if (open) load();
    const t = setInterval(() => open && load(), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [open, partner?.user_id]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !partner) return;
    setSending(true);
    try {
      const r = await api.post("/messages/send", { to_user_id: partner.user_id, text });
      setMessages(m => [...m, r.data]); setText("");
    } finally { setSending(false); }
  };
  if (!partner) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-3xl max-w-lg border-border p-0 overflow-hidden" data-testid="chat-window">
        <div className="px-5 py-4 border-b border-border flex items-center gap-3 bg-card">
          {partner.picture ? <img src={partner.picture} className="w-10 h-10 rounded-full object-cover" alt="" /> : (
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center font-semibold">{partner.name?.[0]}</div>
          )}
          <div>
            <div className="font-display font-semibold">{partner.name}</div>
            <div className="text-xs text-muted-foreground">{partner.role}</div>
          </div>
        </div>
        <div ref={scrollRef} className="h-96 overflow-y-auto bg-muted/30 px-5 py-4 space-y-2">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm">
              <MessageCircle className="w-8 h-8 mb-2 opacity-50" /> Start the conversation
            </div>
          ) : messages.map(m => {
            const mine = m.from_user_id === user.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-4 py-2 rounded-2xl text-sm ${mine ? "bg-primary text-primary-foreground rounded-br-md" : "bg-card text-foreground rounded-bl-md border border-border"}`}>
                  {m.text}
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t border-border bg-card flex gap-2">
          <Input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Write a message..." className="h-11 rounded-full" data-testid="chat-input" />
          <Button onClick={send} disabled={sending || !text.trim()} data-testid="chat-send-btn"
            className="rounded-full bg-primary hover:bg-primary/90 h-11 px-5">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
